"use client"

import { useState } from "react"
import { useFormik } from "formik"
import { ForgotPasswordSchema, VerifyOtpSchema, UpdatePasswordSchema } from "./ForgotPasswordSchema"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast"
import { FiArrowLeft, FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi"
import axios from "axios"
// import "./ForgotPassword.css"
import "./Login.css";

const ForgotPassword = () => {
  const [step, setStep] = useState(1) // Step 1: Email, Step 2: OTP, Step 3: New Password
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"
  const gradientColor = "linear-gradient(135deg, #e5272e 0%, #f59332 100%)"
  const primaryAccentColor = "#e5272e"
  const navigate = useNavigate()

  // Step 1: Send OTP
  const {
    values: emailValues,
    handleBlur: emailHandleBlur,
    handleChange: emailHandleChange,
    handleSubmit: emailHandleSubmit,
    errors: emailErrors,
    touched: emailTouched,
  } = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: ForgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await axios.post(`${baseUrl}auth/send-otp`, {
          email: values.email,
        })
        setEmail(values.email)
        setStep(2)
        toast.success("OTP sent to your email!")
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to send OTP")
      } finally {
        setLoading(false)
      }
    },
  })

  // Step 2: Verify OTP
  const {
    values: otpValues,
    handleBlur: otpHandleBlur,
    handleChange: otpHandleChange,
    handleSubmit: otpHandleSubmit,
    errors: otpErrors,
    touched: otpTouched,
  } = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: VerifyOtpSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await axios.post(`${baseUrl}auth/verify-otp`, {
          email: email,
          otp: values.otp,
        })
        setOtp(values.otp)
        setStep(3)
        toast.success("OTP verified successfully!")
      } catch (error) {
        toast.error(error.response?.data?.message || "Invalid OTP")
      } finally {
        setLoading(false)
      }
    },
  })

  // Step 3: Update Password
  const {
    values: passwordValues,
    handleBlur: passwordHandleBlur,
    handleChange: passwordHandleChange,
    handleSubmit: passwordHandleSubmit,
    errors: passwordErrors,
    touched: passwordTouched,
  } = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: UpdatePasswordSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await axios.post(`${baseUrl}auth/update-password`, {
          email: email,
          otp: otp,
          newPassword: values.newPassword,
        })
        toast.success("Password updated successfully!")
        navigate("/login")
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update password")
      } finally {
        setLoading(false)
      }
    },
  })

  const handleBackClick = () => {
    if (step > 1) {
      setStep(step - 1)
    } else {
      navigate("/login")
    }
  }

  return (
    <>
      <style>
        {`
          .forgot-password-page-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f4f7f9;
            padding: 2rem 0;
          }
          .forgot-password-card {
            background-color: #ffffff;
            border-radius: 16px;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            display: flex;
            width: 100%;
            max-width: 900px;
          }
          .forgot-password-form-section {
            padding: 2.5rem 3rem;
            flex: 1 1 55%;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .forgot-password-visual-section {
            flex: 1 1 45%;
            background-image: ${gradientColor};
            background-size: 200% 200%;
            animation: gradientAnimation 10s ease infinite;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            padding: 2rem;
            text-align: center;
          }
          @keyframes gradientAnimation {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .forgot-password-visual-section h2 {
            font-weight: 700;
            font-size: 2.5rem;
            margin-bottom: 1rem;
            text-shadow: 1px 1px 3px rgba(0,0,0,0.2);
          }
          .forgot-password-visual-section p {
            font-size: 1.1rem;
            opacity: 0.9;
            max-width: 300px;
          }
          .forgot-password-title {
            font-size: 2rem;
            font-weight: 700;
            color: ${primaryAccentColor};
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
          }
          .forgot-password-title svg {
            margin-right: 10px;
          }
          .forgot-password-subtitle {
            font-size: 1rem;
            color: #6c757d;
            margin-bottom: 2rem;
          }
          .form-label-forgot {
            font-weight: 600;
            color: #343a40;
            font-size: 0.9rem;
          }
          .form-control-forgot {
            border-radius: 8px;
            border: 1px solid #ced4da;
            padding: 0.75rem 1rem;
            padding-left: 3rem;
            font-size: 1rem;
            transition: border-color 0.2s ease, box-shadow 0.2s ease;
            width: 100%;
          }
          .form-control-forgot:focus {
            border-color: ${primaryAccentColor};
            box-shadow: 0 0 0 0.2rem rgba(229, 39, 46, 0.2);
            background-color: #fff;
          }
          .input-group-forgot {
            position: relative;
            display: flex;
            align-items: center;
          }
          .input-group-forgot .input-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: #adb5bd;
            font-size: 1.1rem;
            z-index: 2;
          }
          .password-toggle-icon {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: #adb5bd;
            cursor: pointer;
            font-size: 1.2rem;
            z-index: 2;
          }
          .forgot-password-btn-submit {
            background-image: ${gradientColor};
            color: white;
            border: none;
            padding: 0.8rem 1.5rem;
            border-radius: 50px;
            font-weight: 600;
            font-size: 1.05rem;
            transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
            width: 100%;
            margin-top: 1rem;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .forgot-password-btn-submit:hover {
            opacity: 0.85;
            transform: translateY(-2px);
            box-shadow: 0 6px 15px rgba(229, 39, 46, 0.25);
          }
          .forgot-password-btn-submit:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          .back-button {
            display: flex;
            align-items: center;
            color: ${primaryAccentColor};
            background: none;
            border: none;
            cursor: pointer;
            font-weight: 500;
            margin-bottom: 1rem;
            font-size: 0.95rem;
            transition: opacity 0.2s ease;
          }
          .back-button:hover {
            opacity: 0.8;
          }
          .back-button svg {
            margin-right: 0.5rem;
          }
          .error-message {
            color: #dc3545;
            font-size: 0.8rem;
            margin-top: 0.25rem;
          }
          .step-indicator {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2rem;
            font-size: 0.85rem;
            color: #6c757d;
          }
          .step-indicator .step {
            display: flex;
            align-items: center;
            font-weight: 500;
          }
          .step-indicator .step.active {
            color: ${primaryAccentColor};
          }
          .step-indicator .step-dot {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background-color: #e9ecef;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.5rem;
            font-size: 0.75rem;
            font-weight: 700;
          }
          .step-indicator .step.active .step-dot {
            background-color: ${primaryAccentColor};
            color: white;
          }
          .forgot-password-links {
            font-size: 0.85rem;
            text-align: center;
            margin-top: 1.5rem;
          }
          .forgot-password-links a {
            color: ${primaryAccentColor};
            text-decoration: none;
            font-weight: 500;
          }
          .forgot-password-links a:hover {
            text-decoration: underline;
          }

          @media (max-width: 768px) {
            .forgot-password-card {
              flex-direction: column;
            }
            .forgot-password-visual-section {
              min-height: 250px;
              border-radius: 16px 16px 0 0;
            }
            .forgot-password-form-section {
              padding: 2rem 1.5rem;
            }
            .forgot-password-title {
              font-size: 1.8rem;
            }
          }
        `}
      </style>
      <section className="forgot-password-page-container">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <div className="forgot-password-card">
                <div className="forgot-password-form-section">
                  <button type="button" className="back-button" onClick={handleBackClick}>
                    <FiArrowLeft /> Back
                  </button>

                  {/* Step Indicator showing progress through 3 steps */}
                  <div className="step-indicator">
                    <div className={`step ${step >= 1 ? "active" : ""}`}>
                      <div className="step-dot">1</div>
                      Email
                    </div>
                    <div className={`step ${step >= 2 ? "active" : ""}`}>
                      <div className="step-dot">2</div>
                      OTP
                    </div>
                    <div className={`step ${step >= 3 ? "active" : ""}`}>
                      <div className="step-dot">3</div>
                      Password
                    </div>
                  </div>

                  {/* Step 1: Email */}
                  {step === 1 && (
                    <>
                      <h2 className="forgot-password-title">
                        <FiMail /> Reset Password
                      </h2>
                      <p className="forgot-password-subtitle">
                        Enter your email address and we'll send you an OTP to reset your password.
                      </p>
                      <form onSubmit={emailHandleSubmit} noValidate>
                        <div className="mb-3">
                          <label htmlFor="email" className="form-label form-label-forgot">
                            Email Address
                          </label>
                          <div className="input-group-forgot">
                            {/* <FiMail className="input-icon" /> */}
                            <input
                              type="email"
                              className={`form-control form-control-forgot ${
                                emailErrors.email && emailTouched.email ? "is-invalid" : ""
                              }`}
                              id="email"
                              name="email"
                              placeholder="you@example.com"
                              value={emailValues.email}
                              onBlur={emailHandleBlur}
                              onChange={emailHandleChange}
                            />
                          </div>
                          {emailErrors.email && emailTouched.email && (
                            <p className="error-message">{emailErrors.email}</p>
                          )}
                        </div>

                        <button type="submit" className="btn forgot-password-btn-submit" disabled={loading}>
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Sending OTP...
                            </>
                          ) : (
                            "Send OTP"
                          )}
                        </button>
                      </form>
                    </>
                  )}

                  {/* Step 2: OTP Verification */}
                  {step === 2 && (
                    <>
                      <h2 className="forgot-password-title">
                        <FiMail /> Verify OTP
                      </h2>
                      <p className="forgot-password-subtitle">Enter the 6-digit OTP sent to {email}</p>
                      <form onSubmit={otpHandleSubmit} noValidate>
                        <div className="mb-3">
                          <label htmlFor="otp" className="form-label form-label-forgot">
                            One-Time Password
                          </label>
                          <div className="input-group-forgot">
                            {/* <FiLock className="input-icon" /> */}
                            <input
                              type="text"
                              className={`form-control form-control-forgot ${
                                otpErrors.otp && otpTouched.otp ? "is-invalid" : ""
                              }`}
                              id="otp"
                              name="otp"
                              placeholder="000000"
                              maxLength="6"
                              value={otpValues.otp}
                              onBlur={otpHandleBlur}
                              onChange={otpHandleChange}
                            />
                          </div>
                          {otpErrors.otp && otpTouched.otp && <p className="error-message">{otpErrors.otp}</p>}
                        </div>

                        <button type="submit" className="btn forgot-password-btn-submit" disabled={loading}>
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Verifying...
                            </>
                          ) : (
                            "Verify OTP"
                          )}
                        </button>
                      </form>
                    </>
                  )}

                  {/* Step 3: New Password */}
                  {step === 3 && (
                    <>
                      <h2 className="forgot-password-title">
                        <FiLock /> Create New Password
                      </h2>
                      <p className="forgot-password-subtitle">Enter your new password to complete the reset.</p>
                      <form onSubmit={passwordHandleSubmit} noValidate>
                        <div className="mb-3">
                          <label htmlFor="newPassword" className="form-label form-label-forgot">
                            New Password
                          </label>
                          <div className="input-group-forgot">
                            {/* <FiLock className="input-icon" /> */}
                            <input
                              type={showPassword ? "text" : "password"}
                              className={`form-control form-control-forgot ${
                                passwordErrors.newPassword && passwordTouched.newPassword ? "is-invalid" : ""
                              }`}
                              id="newPassword"
                              name="newPassword"
                              placeholder="Enter new password"
                              value={passwordValues.newPassword}
                              onBlur={passwordHandleBlur}
                              onChange={passwordHandleChange}
                            />
                            <span onClick={() => setShowPassword(!showPassword)} className="password-toggle-icon">
                              {showPassword ? <FiEyeOff /> : <FiEye />}
                            </span>
                          </div>
                          {passwordErrors.newPassword && passwordTouched.newPassword && (
                            <p className="error-message">{passwordErrors.newPassword}</p>
                          )}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="confirmPassword" className="form-label form-label-forgot">
                            Confirm Password
                          </label>
                          <div className="input-group-forgot">
                            {/* <FiLock className="input-icon" /> */}
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className={`form-control form-control-forgot ${
                                passwordErrors.confirmPassword && passwordTouched.confirmPassword ? "is-invalid" : ""
                              }`}
                              id="confirmPassword"
                              name="confirmPassword"
                              placeholder="Confirm password"
                              value={passwordValues.confirmPassword}
                              onBlur={passwordHandleBlur}
                              onChange={passwordHandleChange}
                            />
                            <span
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="password-toggle-icon"
                            >
                              {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </span>
                          </div>
                          {passwordErrors.confirmPassword && passwordTouched.confirmPassword && (
                            <p className="error-message">{passwordErrors.confirmPassword}</p>
                          )}
                        </div>

                        <button type="submit" className="btn forgot-password-btn-submit" disabled={loading}>
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Updating...
                            </>
                          ) : (
                            "Update Password"
                          )}
                        </button>
                      </form>
                    </>
                  )}

                  <div className="forgot-password-links">
                    <span>Remember your password? </span>
                    <Link to="/login">Back to Login</Link>
                  </div>
                </div>
                <div className="forgot-password-visual-section d-none d-md-flex">
                  <h2>Booze247</h2>
                  <p>Secure your account with a new password. We've got your back!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ForgotPassword
